import type {
  DashboardDataStatus,
  DashboardNotificationStatus,
  DashboardPopulationUnavailableReason,
} from "@situate/contracts/runtime";
import type { DashboardNotificationSeverity } from "@situate/contracts/runtime";
import type { DeploymentAreaDerivedPopulationMetrics } from "@situate/contracts";

export type DashboardWidgetStatus =
  "loading" | "ready" | "empty" | "not_permitted" | "stale" | "unavailable" | "partial" | "error";

export interface DashboardSourceState<T> {
  status: DashboardWidgetStatus;
  data: T | null;
  observedAt?: string;
}

export interface DashboardPopulationMetrics {
  currentPopulation: number | null;
  peakPopulationToday: number | null;
  peakRecordedAt: string | null;
}

export interface DashboardCapacityMetrics {
  maxCapacity: number | null;
}

export interface DashboardThroughputMetrics {
  throughputToday: number | null;
  throughputSince: string | null;
}

export interface DashboardAreaMetric {
  status: DashboardWidgetStatus;
  value: number | null;
  observedAt?: string;
  unavailableReason?: DashboardPopulationUnavailableReason;
}

export interface DashboardAreaRow {
  id: string;
  name: string;
  derivedPopulationMetrics?: DeploymentAreaDerivedPopulationMetrics | null;
  population: DashboardAreaMetric;
  capacity: DashboardAreaMetric;
  derivedPopulation?: DashboardAreaMetric;
}

/**
 * Dashboard-owned presentation of a notification summary, containing only
 * fields already supplied/derived by `DashboardOverviewResponse`. Distinct
 * from the bell panel's contract-backed `NotificationStreamItem` model --
 * this type is never shared with or merged into the SSE provider's state.
 */
export interface DashboardAlertPreviewItem {
  id: string;
  title: string;
  /** Area display name, or a deployment-wide fallback label. */
  zone: string;
  severity: DashboardNotificationSeverity;
  status: DashboardNotificationStatus;
  /** Formatted display timestamp string, e.g. "11:17 UTC". */
  detectedAt: string;
}

export interface DashboardMonitoringData {
  snapshotAt: string | null;
  timezone: string | null;
  dataStatus: DashboardDataStatus | null;
  population: DashboardSourceState<DashboardPopulationMetrics>;
  capacity: DashboardSourceState<DashboardCapacityMetrics>;
  throughput: DashboardSourceState<DashboardThroughputMetrics>;
  areas: DashboardSourceState<DashboardAreaRow[]>;
  alerts: DashboardSourceState<DashboardAlertPreviewItem[]>;
}

export interface DashboardMonitoringDataState {
  data: DashboardMonitoringData;
  loading: boolean;
  refreshing: boolean;
  error: Error | null;
}

export function createDashboardSourceState<T>(
  status: DashboardWidgetStatus,
  data: T | null = null
): DashboardSourceState<T> {
  return { status, data };
}
